import React from "react";
import { Box } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";

import { Label12Regular } from "components";
import { EMPTY_CELL_VALUE } from "consts";

import { isNullOrUndefined } from "utils";
import { EmissionsAllocationOverviewData } from "api";
import { TableRow } from "../types";
import {
    cellStyles,
    aggregatedCellStyles,
    aggregatedValueStyles,
    aggregatedUnitStyles,
} from "./styles";

export const createHeaderWithEllipsis = (text: string) => (
    <Box sx={{ textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden" }}>
        {text}
    </Box>
);

export const createNumberCell = (accessorKey: string, header: string, size?: number): MRT_ColumnDef<TableRow> => ({
    Header: createHeaderWithEllipsis(header),
    header,
    accessorKey,
    size,
    Cell: ({ cell }: { cell: any }) => {
        const value = cell.getValue();
        if (isNullOrUndefined(value)) {
            return EMPTY_CELL_VALUE;
        }
        return <Box>{(value as number)}</Box>;
    },
});

export const createUnitCell = (
    accessorKey: string,
    header: string,
    unitKey: keyof TableRow,
    aggregationFn?: 'sum',
    size?: number
): MRT_ColumnDef<TableRow> => ({
    Header: createHeaderWithEllipsis(header),
    header,
    accessorKey,
    size,
    enableGrouping: false,
    aggregationFn,
    Cell: ({ cell, row }: { cell: any; row: any }) => {
        const value = cell.getValue();
        if (isNullOrUndefined(value)) {
            return EMPTY_CELL_VALUE;
        }
        const unit = row.original[unitKey];
        return (
            <Box sx={cellStyles}>
                <Box>{(Number(value)?.toFixed(2))}</Box>
                <Label12Regular sx={{ ml: "5px", mt: "2px" }}>{unit}</Label12Regular>
            </Box>
        );
    },
    AggregatedCell: ({ cell, row }: { cell: any; row: any }) => {
        const value = cell.getValue();
        const unit = row.original[unitKey];
        return (
            <Box sx={aggregatedCellStyles}>
                <Box sx={aggregatedValueStyles}>{(value as number).toFixed(4)}</Box>
                <Label12Regular sx={aggregatedUnitStyles}>{unit}</Label12Regular>
            </Box>
        );
    },
});

export const createAggregatedMeasureValueCell = (
    value?: number,
    unit?: string
) => {
    if (isNullOrUndefined(value) || isNullOrUndefined(unit)) {
        return EMPTY_CELL_VALUE;
    }

    return (
        <Box sx={aggregatedCellStyles}>
            <Box sx={aggregatedValueStyles}>{value?.toFixed(4)}</Box>
            <Label12Regular sx={aggregatedUnitStyles}>{unit}</Label12Regular>
        </Box>
    );
};

export const createRenewableUsageCell = (accessorKey: string, header: string): MRT_ColumnDef<TableRow | EmissionsAllocationOverviewData> => ({
    Header: createHeaderWithEllipsis(header),
    header,
    accessorKey,
    enableGrouping: false,
    aggregationFn: 'sum' as const,
    Cell: ({ cell, row }: { cell: any; row: any }) => {
        const value = cell.getValue();
        if (isNullOrUndefined(value)) {
            return EMPTY_CELL_VALUE;
        }
        return <Box>{(value as number)}{' '}{row.original.standardizedUsageUnit}</Box>;
    },
    AggregatedCell: ({ cell, row }: { cell: any; row: any }) => {
        const value = cell.getValue();
        const { standardizedUsageUnit } = row.original;
        return (
            <Box sx={aggregatedCellStyles}>
                <Box sx={aggregatedValueStyles}>{(value as number).toFixed(4)}</Box>
                <Label12Regular sx={aggregatedUnitStyles}>{standardizedUsageUnit}</Label12Regular>
            </Box>
        );
    },
});

export const createEmissionCell = (accessorKey: string, header: string, size?: number): MRT_ColumnDef<TableRow> => ({
    Header: createHeaderWithEllipsis(header),
    header,
    accessorKey,
    enableGrouping: false,
    aggregationFn: 'sum' as const,
    size,
    Cell: ({ cell }: { cell: any }) => {
        const value = cell.getValue();
        if (isNullOrUndefined(value)) {
            return EMPTY_CELL_VALUE;
        }
        return (
            <Box sx={cellStyles}>
                <Box>{(value as number)}</Box>
                <Label12Regular sx={{ ml: "5px", mt: "2px" }}>tCO2eq</Label12Regular>
            </Box>
        );
    },
    AggregatedCell: ({ cell }: { cell: any }) => {
        const value = cell.getValue();
        return (
            <Box sx={aggregatedCellStyles}>
                <Box sx={aggregatedValueStyles}>{(value as number).toFixed(4)}</Box>
                <Label12Regular sx={aggregatedUnitStyles}>tCO2eq</Label12Regular>
            </Box>
        );
    },
});

export const createIntensityCell = (
    accessorKey: string,
    header: string,
    unitKey: keyof TableRow
): MRT_ColumnDef<TableRow> => ({
    Header: createHeaderWithEllipsis(header),
    header,
    accessorKey,
    enableGrouping: false,
    Cell: ({ cell, row }: { cell: any; row: any }) => {
        const value = cell.getValue();
        if (isNullOrUndefined(value)) {
            return EMPTY_CELL_VALUE;
        }
        const unit = row.original[unitKey];
        return (
            <Box sx={cellStyles}>
                <Box>{(value as number)}</Box>
                <Label12Regular sx={{ ml: "4px", mt: "2px" }}>tCO2eq/{unit}</Label12Regular>
            </Box>
        );
    },
    AggregatedCell: ({ cell, row }: { cell: any; row: any }) => {
        const value = cell.getValue();
        const unit = row.original[unitKey];

        if (isNullOrUndefined(value)) {
            return (
                <Box sx={aggregatedCellStyles}>
                    <Box sx={aggregatedValueStyles}>{EMPTY_CELL_VALUE}</Box>
                </Box>
            );
        }

        return (
            <Box sx={aggregatedCellStyles}>
                <Box sx={aggregatedValueStyles}>{(value as number).toFixed(4)}</Box>
                <Label12Regular sx={{ ml: "5px", mt: "2px", ...aggregatedValueStyles }}>tCO2eq/{unit}</Label12Regular>
            </Box>
        );
    },
});